import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Check, RotateCcw, Palette, Eye, Type, LayoutGrid } from "lucide-react";

import Button from "@/components/Button";
import { Card, CardHeader } from "@/components/Card";
import { useApp } from "@/App";
import { school } from "@/theme/school";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — brand customization.
//
// The page that turns the demo from "an app" into "the app a
// school could make its own." Three live controls:
//
//   1. Navy      — the primary surface color. Sidebar active
//                  state, login mark, primary buttons, fee-bar
//                  fills all read from this.
//   2. Gold      — the accent. Login "I" mark, selected-role
//                  checkmark, avatars' initials color.
//   3. Logo mark — a single character the school picks to
//                  stand for itself, shown in the app's chrome.
//
// Every change is applied live via CSS custom properties on the
// app root. No reload, no apply button, no partial commit. A
// director slides a color and the whole app repaints, including
// the header they're looking at and the tab bar below them.
//
// "Save" and "Reset" are the only two buttons. Save persists to
// the store (which just means: stops tracking the draft state).
// Reset restores the values declared in src/theme/school.ts.
// ─────────────────────────────────────────────────────────────────

type Draft = { navy: string; gold: string; mark: string };

export default function Customize() {
  const brandNavy = useApp((s) => s.navy);
  const brandGold = useApp((s) => s.gold);
  const brandMark = useApp((s) => s.mark);
  const setBrand = useApp((s) => s.setBrand);
  const setMark = useApp((s) => s.setMark);

  const [draft, setDraft] = useState<Draft>({
    navy: brandNavy,
    gold: brandGold,
    mark: brandMark,
  });

  // The page applies the draft on every keystroke, not on save.
  // The store is updated too — because that's how the rest of
  // the app stays in sync — but the draft is what drives the
  // "Reset" button's enabled state.
  function apply(next: Partial<Draft>) {
    const merged = { ...draft, ...next };
    setDraft(merged);
    setBrand({ navy: merged.navy, gold: merged.gold });
  }

  const dirty = useMemo(
    () =>
      draft.navy.toLowerCase() !== school.colors.navy.toLowerCase() ||
      draft.gold.toLowerCase() !== school.colors.gold.toLowerCase() ||
      draft.mark !== school.logo.mark,
    [draft]
  );

  function reset() {
    const fresh: Draft = {
      navy: school.colors.navy,
      gold: school.colors.gold,
      mark: school.logo.mark,
    };
    setDraft(fresh);
    setBrand({ navy: fresh.navy, gold: fresh.gold });
    setMark(fresh.mark);
  }

  return (
    <div className="px-5 pb-8 pt-5">
      {/* ─── Heading ───────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <p className="text-ios-footnote text-ios-secondary">
          Make it yours
        </p>
        <h1 className="mt-0.5 text-ios-title-1 font-bold tracking-ios text-ios-label">
          School brand
        </h1>
        <p className="mt-1 text-ios-caption-1 text-ios-secondary">
          Changes apply live across the whole app
        </p>
      </motion.div>

      {/* ─── Live preview ────────────────────────────────────
          A miniature of the app chrome, using the current
          draft colors. This is what makes the page feel
          responsive — a director doesn't have to switch tabs
          to see the effect of a color change. */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, delay: 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="mt-4 overflow-hidden rounded-ios-lg bg-ios-card p-4 shadow-ios"
      >
        <div className="flex items-center gap-2 mb-3">
          <Eye size={14} strokeWidth={2.4} className="text-ios-secondary" absoluteStrokeWidth />
          <span className="text-ios-caption-1 font-semibold uppercase tracking-wider text-ios-secondary">
            Preview
          </span>
        </div>

        <div className="flex items-center gap-3 rounded-ios-md bg-ios-fill p-3">
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-ios-md text-[18px] font-bold"
            style={{
              backgroundColor: draft.navy,
              color: draft.gold,
            }}
          >
            {draft.mark}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-ios-subhead font-semibold" style={{ color: draft.navy }}>
              {school.name}
            </p>
            <p className="truncate text-ios-caption-1 text-ios-secondary">
              {school.motto}
            </p>
          </div>
          <div
            className="rounded-ios-pill px-2.5 py-1 text-ios-caption-2 font-semibold"
            style={{ backgroundColor: draft.navy, color: "#FFFFFF" }}
          >
            Term 2
          </div>
        </div>
      </motion.div>

      {/* ─── Navy picker ───────────────────────────────────── */}
      <ColorSection
        index={1}
        icon={Palette}
        label="Primary color"
        description="Used for the login mark, active tabs, primary buttons, and fee charts."
        value={draft.navy}
        presets={NAVY_PRESETS}
        onChange={(navy) => apply({ navy })}
      />

      {/* ─── Gold picker ───────────────────────────────────── */}
      <ColorSection
        index={2}
        icon={Type}
        label="Accent color"
        description="Used for the school mark, selected-state checkmarks, and highlights."
        value={draft.gold}
        presets={GOLD_PRESETS}
        onChange={(gold) => apply({ gold })}
      />

      {/* ─── Mark picker ───────────────────────────────────── */}
      <Card className="mt-5" index={3}>
        <CardHeader
          title="School mark"
          subtitle="A single character shown in the app chrome"
        />
        <div className="px-4 pb-4">
          <div className="grid grid-cols-6 gap-2">
            {MARKS.map((m) => {
              const active = draft.mark === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setDraft((d) => ({ ...d, mark: m }));
                    setMark(m);
                  }}
                  className="ios-pressable grid aspect-square place-items-center rounded-ios-md text-[18px] font-bold transition-colors"
                  style={{
                    backgroundColor: active ? draft.navy : "rgba(120, 120, 128, 0.10)",
                    color: active ? draft.gold : "#3C3C43",
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* ─── Hint ──────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="mt-5 flex items-start gap-2 rounded-ios-md px-3 py-2.5"
        style={{ backgroundColor: "rgba(0, 122, 255, 0.08)" }}
      >
        <LayoutGrid
          size={14}
          strokeWidth={2.4}
          className="mt-0.5 shrink-0"
          style={{ color: "#007AFF" }}
          absoluteStrokeWidth
        />
        <p className="text-ios-caption-1 leading-snug" style={{ color: "#0A2540" }}>
          Tap any color swatch to apply it instantly. The header,
          tab bar, and primary buttons update as you go.
        </p>
      </motion.div>

      {/* ─── Actions ─────────────────────────────────────────
          Reset is always available; Save is disabled when the
          draft matches the theme's declared values. The intent
          is that "Save" means "make this the permanent brand",
          which for the demo is a no-op — the store is the
          brand. The button exists so the interaction reads
          correctly. */}
      <div className="mt-6 flex gap-2">
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          leadingIcon={RotateCcw}
          onClick={reset}
        >
          Reset
        </Button>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          leadingIcon={Check}
          disabled={!dirty}
          onClick={() => setDraft((d) => ({ ...d }))}
        >
          Save
        </Button>
      </div>

      <p className="mt-4 text-center text-ios-caption-2 text-ios-secondary">
        Brand preview · {school.name}
      </p>
    </div>
  );
}

// ─── ColorSection ───────────────────────────────────────────────

type ColorSectionProps = {
  index: number;
  icon: typeof Palette;
  label: string;
  description: string;
  value: string;
  presets: { hex: string; name: string }[];
  onChange: (hex: string) => void;
};

function ColorSection({
  index,
  icon: Icon,
  label,
  description,
  value,
  presets,
  onChange,
}: ColorSectionProps) {
  return (
    <Card className="mt-5" index={index}>
      <CardHeader title={label} subtitle={description} />
      <div className="px-4 pb-4">
        {/* The current value, as a swatch + a hex readout. */}
        <div className="flex items-center gap-3">
          <div
            className="h-12 w-12 shrink-0 rounded-ios-md shadow-ios-sm"
            style={{ backgroundColor: value }}
          />
          <div className="min-w-0">
            <p className="text-ios-footnote text-ios-secondary">Current</p>
            <p className="font-tabular text-ios-subhead font-semibold text-ios-label uppercase">
              {value}
            </p>
          </div>
          <span className="ml-auto grid h-8 w-8 place-items-center rounded-full bg-ios-fill text-ios-secondary">
            <Icon size={15} strokeWidth={2} absoluteStrokeWidth />
          </span>
        </div>

        {/* Preset swatches. Eight per row on wide screens, six
            on narrow — the CSS handles the wrapping without a
            media query by allowing the row to wrap naturally. */}
        <div className="mt-4 flex flex-wrap gap-2">
          {presets.map((p) => {
            const active = value.toLowerCase() === p.hex.toLowerCase();
            return (
              <button
                key={p.hex}
                type="button"
                onClick={() => onChange(p.hex)}
                aria-label={p.name}
                title={p.name}
                className="ios-pressable grid h-9 w-9 place-items-center rounded-full"
                style={{ backgroundColor: p.hex }}
              >
                {active && (
                  <motion.span
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 26 }}
                    className="grid h-full w-full place-items-center rounded-full"
                    style={{ boxShadow: "inset 0 0 0 2px #FFFFFF" }}
                  >
                    <Check
                      size={14}
                      strokeWidth={3}
                      style={{ color: contrastOn(p.hex) }}
                      absoluteStrokeWidth
                    />
                  </motion.span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// ─── Data ───────────────────────────────────────────────────────

const NAVY_PRESETS = [
  { hex: "#0A2540", name: "Iratation Navy" },
  { hex: "#1A3A5C", name: "Midnight" },
  { hex: "#0B3D2E", name: "Forest" },
  { hex: "#3D1F5C", name: "Royal" },
  { hex: "#5C1A2E", name: "Maroon" },
  { hex: "#1C1C1E", name: "Graphite" },
  { hex: "#0E4C6B", name: "Ocean" },
  { hex: "#3A3A3C", name: "Slate" },
];

const GOLD_PRESETS = [
  { hex: "#F5C842", name: "Iratation Gold" },
  { hex: "#FAD97A", name: "Sunrise" },
  { hex: "#E8B33D", name: "Amber" },
  { hex: "#E5A663", name: "Copper" },
  { hex: "#D4A520", name: "Brass" },
  { hex: "#C9A227", name: "Old Gold" },
  { hex: "#F4D03F", name: "Lemon" },
  { hex: "#B88A44", name: "Bronze" },
];

const MARKS = ["I", "H", "S", "★", "◆", "▲"];

// ─── Contrast ───────────────────────────────────────────────────
//
// Picks black or white for the checkmark on a preset swatch,
// based on the swatch's relative luminance. This is a simplified
// WCAG contrast check — good enough to keep the check readable
// on every preset in the list above, and cheap enough to run on
// every render without memoising.

function contrastOn(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  // sRGB relative luminance, per WCAG 2.1.
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.5 ? "#000000" : "#FFFFFF";
}